// Tournaments → Cups (contracts/tournaments.md §9): every cup (GET /cups) with its status and window, entrants,
// completion (finished ÷ entrants) and flagged entries (flags or disqualified), filtered by status and kind. Cups
// whose gift-card winners wait for a confirmation come from the prize queue's unconfirmed awards. A cup hidden
// from players carries a "Hidden" pill; while cups.hidden is on, every cup is out of the app. Rows open the cup
// (/tournaments/:id); editors create cups (Weekly Cups normally come from the template in Settings).

import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { type AdminCupListItem, useAwards, useCups } from "../../api/cups";
import { useConfig } from "../../api/queries";
import { useAuth, useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Segmented, Select } from "../../components/ui";
import { CUP_KINDS, CUP_STATUSES, type CupKind, type CupStatus, KIND_LABEL, readCupsConfig, STATUS_TONE } from "../../lib/cups";
import { fmtInt, fmtPct } from "../../lib/format";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import { cupTitle, cupWhen } from "./components";
import c from "./cups.module.css";

type StatusFilter = "all" | CupStatus;
type KindFilter = "all" | CupKind;

/** Newest first within a status: open, then scheduled (soonest first), then the rest by close. */
const ORDER: Record<CupStatus, number> = { open: 0, closed: 1, scheduled: 2, draft: 3, finalized: 4, cancelled: 5 };
const sortCups = (a: AdminCupListItem, b: AdminCupListItem) =>
  ORDER[a.status] - ORDER[b.status] || (a.status === "scheduled" ? a.opensAt.localeCompare(b.opensAt) : b.closesAt.localeCompare(a.closesAt));

export function CupsPage() {
  const canEdit = useCan("cups.edit");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const list = useCups();
  const config = useConfig();
  const money = useAwards();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [kind, setKind] = useState<KindFilter>("all");
  const settings = config.data ? readCupsConfig(config.data.values) : null;

  const items = list.data?.items ?? [];
  const ofKind = kind === "all" ? items : items.filter((x) => x.kind === kind);
  const shown = (status === "all" ? ofKind : ofKind.filter((x) => x.status === status)).sort(sortCups);
  const count = (f: StatusFilter) => (f === "all" ? ofKind.length : ofKind.filter((x) => x.status === f).length);
  // Finalized cups with prizes of money value not confirmed yet (the queue lists them with confirmed: false).
  const unconfirmed = new Set((money.data?.items ?? []).filter((a) => !a.confirmed).map((a) => a.cupId));
  const toConfirm = (cup: AdminCupListItem) => cup.status === "finalized" && !cup.confirmedAt && unconfirmed.has(cup.id);
  const waiting = items.filter(toConfirm);

  return (
    <>
      <PageHeader
        title="Tournaments"
        meta={
          canEdit ? (
            <span>Play-anytime cups: everyone plays their own matched set once inside the window and is ranked when it closes.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · editing cups needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => navigate("/tournaments/new")} data-testid="new-cup">
              New cup
            </Button>
          ) : null
        }
      />
      {settings && !settings.enabled ? (
        <div className={m.notice} data-tone="warning" style={{ marginBottom: 16 }} data-testid="cups-off">
          <strong>Cups are off for players (cups.enabled).</strong>
          <span>
            The app hides Tournaments until it's turned on in <Link to="/tournaments/settings">Settings</Link>. Cups still run and can be prepared.
          </span>
        </div>
      ) : settings?.hidden ? (
        <div className={m.notice} data-tone="warning" style={{ marginBottom: 16 }} data-testid="cups-hidden">
          <strong>Tournaments are hidden in the app (cups.hidden).</strong>
          <span>
            Players see no cups and get no cup pushes; the cups below keep running. Show them again in <Link to="/tournaments/settings">Settings</Link>.
          </span>
        </div>
      ) : null}
      {waiting.length ? (
        <div className={m.notice} data-tone="info" style={{ marginBottom: 16 }} data-testid="awaiting-confirmation">
          <strong>
            {waiting.length === 1 ? "1 cup waits" : `${waiting.length} cups wait`} for its results to be confirmed: {waiting.map(cupTitle).join(", ")}.
          </strong>
          <span>Gift-card winners can claim only after an admin reviews the flagged top entries and confirms.</span>
        </div>
      ) : null}
      <div className={c.filters}>
        <Segmented
          label="Status"
          value={status}
          onChange={setStatus}
          options={(["all", ...CUP_STATUSES] as const).map((f) => ({ value: f, label: `${f === "all" ? "All" : f[0]!.toUpperCase() + f.slice(1)} · ${count(f)}` }))}
        />
        <Select value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} aria-label="Kind" data-testid="kind-filter" style={{ width: 180 }}>
          <option value="all">Every kind</option>
          {CUP_KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </Select>
      </div>
      <Card padded={false}>
        {list.isPending ? (
          <Loading rows={6} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : shown.length === 0 ? (
          <EmptyState title="No cups here">{items.length ? "Nothing matches these filters." : "Weekly Cups appear once the template is on; editors create special cups and Championships."}</EmptyState>
        ) : (
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Cup</th>
                  <th>Status</th>
                  <th data-align="right">Entrants</th>
                  <th>Completion</th>
                  <th data-align="right">Flags</th>
                  <th className={c.wideOnly}>Format</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((cup) => (
                  <tr key={cup.id} data-clickable data-testid="cup-row" data-status={cup.status} onClick={(e) => !(e.target as HTMLElement).closest("a, button") && navigate(`/tournaments/${cup.id}`)}>
                    <td>
                      <div className={c.cupCell}>
                        <div className={c.cellText}>
                          <button type="button" className={c.title} onClick={() => navigate(`/tournaments/${cup.id}`)}>
                            {cupTitle(cup)}
                          </button>
                          <span>
                            {KIND_LABEL[cup.kind]}
                            {cup.number && !cupTitle(cup).includes(String(cup.number)) ? ` #${cup.number}` : ""}
                            {cup.final ? ` · ${cup.final.mode} final` : ""}
                            {cup.content.en?.prizeHeadline ? ` · ${cup.content.en.prizeHeadline}` : ""}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={c.statusCell}>
                      <Chip tone={STATUS_TONE[cup.status]}>{cup.status}</Chip>
                      {cup.hidden ? (
                        <>
                          {" "}
                          <span data-testid="hidden-pill" title="Hidden from players: not listed in the app, no new entries, no pushes">
                            <Chip tone="violet">Hidden</Chip>
                          </span>
                        </>
                      ) : null}
                      {toConfirm(cup) ? (
                        <>
                          {" "}
                          <Chip tone="warning">to confirm</Chip>
                        </>
                      ) : null}
                      <span className={c.when}>{cupWhen(cup)}</span>
                    </td>
                    <td data-align="right" className={tableStyles.mono}>
                      {cup.entrants === 0 && (cup.status === "scheduled" || cup.status === "draft" || cup.status === "cancelled") ? <span className={c.dim}>—</span> : fmtInt(cup.entrants)}
                    </td>
                    <td>
                      {cup.entrants === 0 ? (
                        <span className={c.dim}>—</span>
                      ) : (
                        <span className={c.completion}>
                          <span className={c.meter}>
                            <span style={{ width: `${Math.round((cup.finished / cup.entrants) * 100)}%` }} />
                          </span>
                          <span className={c.mono}>{fmtPct(cup.finished / cup.entrants)}</span>
                        </span>
                      )}
                    </td>
                    <td data-align="right" className={tableStyles.mono} data-testid="cup-flags">
                      {cup.flagged ? <span className={c.flagCount}>{fmtInt(cup.flagged)}</span> : <span className={c.dim}>0</span>}
                    </td>
                    <td className={`${c.dim} ${c.wideOnly}`} title={cup.brackets.map((b) => b.id).join(" · ")}>
                      {cup.brackets.length} {cup.brackets.length === 1 ? "bracket" : "brackets"} · set of {cup.setSize}
                      <span className={c.when}>
                        {cup.final ? `${cup.final.mode} final` : `week ${cup.week.slice(5)}`}
                        {cup.qualifying ? ` · qualifying ${cup.qualifying.month}` : ""}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
